import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { useScrollLock } from "../lib/scrollLock";
import { useTabActive } from "../lib/tabActive";
import "./Drawer.css";

// ── Stack context ────────────────────────────────────────────────────────

interface DrawerEntry {
  id: string;
  close: () => void; // stable closer (ref'ten okur)
  dismissible: boolean; // backdrop tıklaması VE ESC ile kapanır mı
}

interface DrawerStackCtx {
  entries: DrawerEntry[];
  register: (entry: DrawerEntry) => void;
  unregister: (id: string) => void;
}

const Ctx = createContext<DrawerStackCtx | null>(null);

export function DrawerStackProvider({ children }: { children: ReactNode }) {
  const [entries, setEntries] = useState<DrawerEntry[]>([]);

  const register = useCallback((entry: DrawerEntry) => {
    setEntries((s) => (s.some((x) => x.id === entry.id) ? s : [...s, entry]));
  }, []);
  const unregister = useCallback((id: string) => {
    setEntries((s) => s.filter((x) => x.id !== id));
  }, []);

  // Body scroll lock while any drawer açık — REF SAYAÇLI ortak kilit (lib/scrollLock).
  // Kendi save/restore'unu yapmak, dosya önizleme modaliyle iç içe girdiğinde sayfayı
  // KALICI olarak kilitliyordu; gerekçe scrollLock.ts başlığında.
  useScrollLock(entries.length > 0);

  // Backdrop tıklayınca en üstteki drawer'ı kapat (dismissible değilse dokunma — veri kaybı önlenir)
  const closeTop = () => {
    const top = entries[entries.length - 1];
    if (top && top.dismissible) top.close();
  };

  return (
    <Ctx.Provider value={{ entries, register, unregister }}>
      {children}
      {entries.length > 0
        ? createPortal(
            <div className="drawer-backdrop" onClick={closeTop} aria-hidden="true" />,
            document.body
          )
        : null}
    </Ctx.Provider>
  );
}

// ── Drawer component ──────────────────────────────────────────────────────

const OFFSET_PX = 40;

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  size?: "sm" | "md" | "lg" | "xl" | "xxl" | "3xl";
  /** Preset boyut sınıfını EZEN kesin genişlik (px). Yalnız özel durumlar için; responsive maxWidth otomatik. */
  widthPx?: number;
  /** Tam ekran (100vw) — geniş çalışma alanı gerektiren tasarım/builder ekranları için. size/widthPx'i ezer. */
  fullScreen?: boolean;
  /** Backdrop (boşluk) tıklamasıyla kapansın mı. false = yalnız X/footer ile kapanır (veri girişi olan formlar). Varsayılan true. */
  dismissible?: boolean;
  children: ReactNode;
  footer?: ReactNode;
}

export function Drawer({ open, onClose, title, subtitle, size = "md", widthPx, fullScreen = false, dismissible = true, children, footer }: DrawerProps) {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("Drawer, DrawerStackProvider içinde olmalı");

  const id = useId();
  const [mounted, setMounted] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const tabActive = useTabActive();

  // onClose'u ref'te tut — her render güncellenir, identity değişmez.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const stableClose = useCallback(() => onCloseRef.current(), []);

  const { register, unregister, entries } = ctx;

  // Register / unregister. `tabActive` ŞART: yığın GLOBAL ve düz bir listedir; pasif
  // sekmenin drawer'ı burada kayıtlı kalsaydı (a) ESC/backdrop "en üstteki"yi kapatırken
  // kullanıcının GÖREMEDİĞİ paneli kapatırdı, (b) "üsttekileri kapat" düğmesi başka
  // sekmede yarım doldurulmuş formu kapatırdı, (c) body kaydırma kilidi asılı kalırdı.
  // Sekme geri geldiğinde yeniden kaydolur — durum kaybı yok, yalnız kayıt askıya alınır.
  useEffect(() => {
    if (!open || !tabActive) return;
    register({ id, close: stableClose, dismissible });
    return () => unregister(id);
  }, [open, tabActive, id, register, unregister, stableClose, dismissible]);

  // Enter animasyonu
  useEffect(() => {
    if (!open) {
      setMounted(false);
      return;
    }
    const raf = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(raf);
  }, [open]);

  const myIndex = entries.findIndex((e) => e.id === id);
  const total = entries.length;
  const isTop = myIndex >= 0 && myIndex === total - 1;

  // ESC sadece en üstteki drawer'ı kapatır — `dismissible={false}` ise ESC de kapatmaz:
  // aksi halde çok adımlı formlarda (personel kartı, sevkiyat sihirbazı) tek tuşla
  // kaydedilmemiş veri uçuyordu. Backdrop zaten dismissible'a bakıyordu, ESC bakmıyordu.
  useEffect(() => {
    if (!open || !isTop || !dismissible) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCloseRef.current();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, isTop, dismissible]);

  // Açılınca panele odaklan (a11y)
  useEffect(() => {
    if (open && mounted && isTop) panelRef.current?.focus();
  }, [open, mounted, isTop]);

  // PASİF SEKMEDE DOM'A BASILMAZ — portal `document.body`ye gider ve sekme panelinin
  // `display:none` gizlemesi ona ULAŞMAZ. `null` dönmek DOM'u düşürür; drawer'ı AÇAN
  // durum ve içindeki form değerleri sayfanın React state'inde YAŞAMAYA DEVAM eder,
  // sekmeye dönüldüğünde panel aynı değerlerle yeniden çizilir.
  if (!open || !tabActive) return null;

  const depthFromTop = myIndex >= 0 ? total - 1 - myIndex : 0;
  const offset = depthFromTop * OFFSET_PX;
  const zIndex = 201 + (myIndex >= 0 ? myIndex : 0) * 2;

  // Üstteki katmanları kapat (peek/cover tıklaması)
  const closeAbove = () => {
    if (myIndex < 0) return;
    entries.slice(myIndex + 1).forEach((e) => e.close());
  };

  return createPortal(
    <div
      ref={panelRef}
      className={`drawer drawer-${size} ${fullScreen ? "drawer-full" : ""} ${isTop ? "is-top" : "is-under"}`}
      style={{ transform: mounted ? `translateX(-${offset}px)` : "translateX(100%)", zIndex, ...(fullScreen ? {} : widthPx ? { width: widthPx, maxWidth: "96vw" } : {}) }}
      role="dialog"
      aria-modal={isTop}
      aria-label={title}
      tabIndex={-1}
    >
      <header className="drawer-head">
        <div>
          <h2 className="ttl">{title}</h2>
          {subtitle ? <p className="sub">{subtitle}</p> : null}
        </div>
        <button type="button" className="drawer-close" onClick={onClose} aria-label="Kapat">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>
      </header>
      <div className="drawer-body">{children}</div>
      {footer ? <footer className="drawer-foot">{footer}</footer> : null}
      {!isTop ? (
        <button
          type="button"
          className="drawer-cover"
          onClick={closeAbove}
          aria-label="Üstteki panelleri kapat"
          title="Bu panele dön"
        />
      ) : null}
    </div>,
    document.body
  );
}
